/**
 * Breakdown table for the AI usage dashboard.
 *
 * Purely presentational: `rows` are already filtered and grouped by the
 * caller (see `usageAggregation.ts`). The one rule that matters here is
 * cost rendering -- `row.costUsd === null` MUST render an em dash, never
 * "$0.00" (null means "no cost applies", e.g. local-model usage).
 *
 * The footer sums the rows actually displayed -- there is no `totals`
 * field from the backend (`SummaryResponse` is exactly `{ rows,
 * scope_degraded }`), so this is explicitly labeled as a total of the
 * shown rows, not a server-computed grand total.
 */

import { type ReactElement } from 'react';
import { formatCost, formatRequests, formatTokens, sumNullableCost } from './usageFormat';
import type { UsageDimension, UsageDisplayRow } from './types';

const DIMENSION_LABELS: Record<UsageDimension, string> = {
  user: 'User',
  project: 'Project',
  model: 'Model',
  provider: 'Provider',
  client: 'Client',
};

interface UsageTableProps {
  rows: UsageDisplayRow[];
  groupBy: UsageDimension[];
}

/**
 * What a dimension cell says. `user` and `project` group by an opaque id
 * and carry a resolved label (email / project path) beside it -- the
 * label is what a person can read, so it wins. The id is the fallback,
 * not a failure: an id with no label (a deleted user, a hand-run probe)
 * is still more useful shown than replaced by an em dash, which would
 * read as "no user at all".
 */
function dimensionText(row: UsageDisplayRow, dim: UsageDimension): string {
  if (dim === 'user') return row.userLabel ?? row.user ?? '—';
  if (dim === 'project') return row.projectLabel ?? row.project ?? '—';
  return row[dim] ?? '—';
}

export function UsageTable({ rows, groupBy }: UsageTableProps): ReactElement {
  const dims = groupBy.length > 0 ? groupBy : (['user'] as UsageDimension[]);

  if (rows.length === 0) {
    return (
      <div className="usage-table__empty" role="status">
        No usage in this range.
      </div>
    );
  }

  const totalCost = sumNullableCost(rows.map((row) => row.costUsd));

  return (
    <div className="usage-table__scroll">
      <table className="usage-table">
        <thead>
          <tr>
            {dims.map((dim) => (
              <th key={dim}>{DIMENSION_LABELS[dim]}</th>
            ))}
            <th className="usage-table__num">Tokens in</th>
            <th className="usage-table__num">Tokens out</th>
            <th className="usage-table__num">Cache read</th>
            <th className="usage-table__num">Cache write</th>
            <th className="usage-table__num">Requests</th>
            <th className="usage-table__num">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // Rows have no stable id -- they're a client-side aggregation
            // keyed by the selected group-by dimensions, so that is the key.
            const rowKey = dims.map((dim) => row[dim] ?? '').join('\0');
            return (
              <tr key={rowKey}>
                {dims.map((dim) => (
                  <td key={dim} className="usage-table__dim">
                    {dimensionText(row, dim)}
                  </td>
                ))}
                <td className="usage-table__num">{formatTokens(row.tokensIn)}</td>
                <td className="usage-table__num">{formatTokens(row.tokensOut)}</td>
                <td className="usage-table__num">{formatTokens(row.cacheReadTokens)}</td>
                <td className="usage-table__num">{formatTokens(row.cacheWriteTokens)}</td>
                <td className="usage-table__num">{formatRequests(row.requests)}</td>
                <td className="usage-table__num">{formatCost(row.costUsd)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="usage-table__foot-label" colSpan={dims.length}>
              Total of {rows.length} {rows.length === 1 ? 'row' : 'rows'} shown
            </td>
            <td className="usage-table__num">
              {formatTokens(rows.reduce((sum, row) => sum + row.tokensIn, 0))}
            </td>
            <td className="usage-table__num">
              {formatTokens(rows.reduce((sum, row) => sum + row.tokensOut, 0))}
            </td>
            <td className="usage-table__num">
              {formatTokens(rows.reduce((sum, row) => sum + row.cacheReadTokens, 0))}
            </td>
            <td className="usage-table__num">
              {formatTokens(rows.reduce((sum, row) => sum + row.cacheWriteTokens, 0))}
            </td>
            <td className="usage-table__num">
              {formatRequests(rows.reduce((sum, row) => sum + row.requests, 0))}
            </td>
            <td className="usage-table__num">{formatCost(totalCost)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
